import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  TableCell,
  Table,
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  Chip,
  Menu,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid2,
} from '@mui/material';
import { MoreVertical, Pen, Trash2 } from 'lucide-react';
import FormEditorDialog from './FormEditorDialog';
import { Questionnaire } from '../../../types/questionnaire.types';
import { questionnaireService } from '../../../services/questionnaire.service';
import { usersService } from '../../../services/users.service';
import { useAuth } from '../../../contexts/AuthContext';
import { getRegionById } from '../../../services/regions.services';
import { Fair, Region } from '../../../types/fair.types';
import { getFairs } from '../../../services/fairs.services';

const FormManagement: React.FC = () => {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [filteredQuestionnaires, setFilteredQuestionnaires] = useState<
    Questionnaire[]
  >([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [formDialogOpen, setformDialogOpen] = useState(false);
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);

  const [changeStatus, setChangeStatus] = useState<string | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null);

  const [selectedQuestionnaireID, setSelectedQuestionnaireID] =
    useState<string>('-1');

  const [region, setRegion] = useState<Region | null>(null);
  const [fairs, setFairs] = useState<Fair[] | null>(null);
  const { authStatus } = useAuth();

  const fetchUserInfo = useCallback(async () => {
    setLoading(true);
    try {
      const userId = authStatus.user?.uid;

      if (!userId) {
        setError('User information not available.');
        setLoading(false);
        return;
      }

      const userRegionId = await usersService.getUserRegion(userId);
      const userRegion = await getRegionById(userRegionId);

      if (!userRegion) {
        setError('You are not associated with any region.');
        setLoading(false);
        return;
      }

      setRegion({ ...userRegion });
    } catch (error) {
      console.error('Error fetching regions:', error);
      setError('Failed to load region information');
    } finally {
      setLoading(false);
    }
  }, [authStatus.user?.uid]);

  useEffect(() => {
    fetchUserInfo();
  }, [fetchUserInfo]);

  const fetchQuestionnaires = useCallback(async () => {
    setLoading(true);
    try {
      if (region?.id) {
        const subscribe =
          questionnaireService.subscribeToQuestionnairesByRegion(
            (updatedQuestionnaires) => {
              setQuestionnaires(updatedQuestionnaires);
              setLoading(false);
            },
            region.id
          );
        return () => subscribe();
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      setError('Failed to load region information');
    } finally {
      setLoading(false);
    }
  }, [region?.id]);

  useEffect(() => {
    fetchQuestionnaires();
  }, [region, fetchQuestionnaires]);

  const fetchFairs = useCallback(async () => {
    try {
      if (region) {
        setFairs(await getFairs(region.id));
      }
    } catch (error) {
      console.error('Error fetching fairs:', error);
      setError('Failed to load fair information');
    }
  }, [region]);

  useEffect(() => {
    fetchFairs();
  }, [region, fetchFairs]);

  // Filter questionnaires based on search term and status
  useEffect(() => {
    if (!questionnaires) return;

    let filtered = [...questionnaires];
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(
        (questionnaire) => questionnaire?.status === statusFilter
      );
    }

    // Apply search term
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((questionnaire) => {
        if (!questionnaire) return false;

        const title = questionnaire.title?.toLowerCase() || '';

        return title.includes(searchLower);
      });
    }

    setFilteredQuestionnaires(filtered);
  }, [questionnaires, statusFilter, searchTerm]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    questionnaireID: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedQuestionnaireID(questionnaireID);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'Required':
        return 'success';
      case 'Optional':
        return 'warning';
      case 'Draft':
        return 'error';
      default:
        return 'default';
    }
  };

  const createNewForm = async (fairID: string) => {
    try {
      if (region) {
        questionnaireService.createQuestionnaire('New Form', region.id, fairID);
      } else {
        setError('Region not available.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error creating form:', error);
      setError('Failed to create forrm');
    }
  };

  const getTimeDate = (date: Date) => {
    const dateString = date.toLocaleDateString();
    const timeString = date.toLocaleTimeString();
    const ampm = timeString.substring(timeString.length - 2, timeString.length);
    const justTimeString = timeString.substring(0, timeString.length - 6);
    return justTimeString + ' ' + ampm + ' ' + dateString;
  };

  const confirmStatusChange = () => {
    try {
      if (changeStatus !== '') {
        questionnaireService.updateQuestionnaire(selectedQuestionnaireID, {
          status: changeStatus,
        } as Partial<Questionnaire>);
      }
    } catch {
      console.log('Error changing form status');
      setError('Error changing form status');
    }
  };

  //Screen loading and error handling
  if (loading) {
    return <Typography>Loading region information...</Typography>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!region) {
    return <Alert severity="info">No region information found.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Form Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
      {/* Filters section */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search forms"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="Required">Required</MenuItem>
              <MenuItem value="Optional">Optional</MenuItem>
              <MenuItem value="Draft">Draft</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Questionnaires Table */}
      {fairs &&
        fairs.map((fair) => (
          <Box>
            <Grid2 container spacing={1} p={1} pt={3}>
              <Grid2
                display="flex"
                justifyContent="left"
                alignItems="center"
                size="grow"
              >
                <Typography
                  sx={{ flex: '1 1 100%' }}
                  variant="h6"
                  id="tableTitle"
                  component="div"
                >
                  {fair.name}
                </Typography>
              </Grid2>
              <Grid2 display="flex" justifyContent="right" alignItems="center">
                <Button
                  variant="contained"
                  onClick={() => createNewForm(fair.id)}
                >
                  Create Form
                </Button>
              </Grid2>
            </Grid2>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredQuestionnaires
                    .filter((obj) => obj.fairID === fair.id)
                    .map(
                      (questionnaire) =>
                        questionnaire && (
                          <TableRow key={questionnaire.id}>
                            <TableCell>{questionnaire.title}</TableCell>
                            <TableCell>
                              <Chip
                                label={questionnaire.status}
                                color={getStatusChipColor(questionnaire.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {getTimeDate(questionnaire.createdAt)}
                            </TableCell>
                            <TableCell>
                              {getTimeDate(questionnaire.updatedAt)}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                onClick={(e) =>
                                  handleMenuOpen(e, questionnaire.id)
                                }
                              >
                                <MoreVertical size={18} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => setformDialogOpen(true)}>
          <Pen size={18} style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => setChangeStatusDialogOpen(true)}>
          <Pen size={18} style={{ marginRight: 8 }} />
          Change Status
        </MenuItem>
        <MenuItem
          onClick={() =>
            selectedQuestionnaireID &&
            (questionnaireService.deleteQuestionnaire(selectedQuestionnaireID),
            handleMenuClose())
          }
        >
          <Trash2 size={18} style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>

      <FormEditorDialog
        open={formDialogOpen}
        questionnaireID={selectedQuestionnaireID}
        selectedQuestionnaire={
          questionnaires.filter((obj) => obj.id === selectedQuestionnaireID)[0]
        }
        onClose={() => setformDialogOpen(false)}
      />

      {/* Form status change dialog */}
      <Dialog
        open={changeStatusDialogOpen}
        onClose={() => setChangeStatusDialogOpen(false)}
      >
        <DialogTitle>Change Status</DialogTitle>
        <DialogContent>
          <DialogContentText>Change form status to:</DialogContentText>
          <Typography textAlign={'center'} sx={{ mb: 1, p: 2, minWidth: 250 }}>
            <FormControl fullWidth>
              <InputLabel id="statusSelect">Status</InputLabel>
              <Select
                labelId="statusSelect"
                id="statusSelect"
                label="Status"
                value={changeStatus}
                onChange={(e) => setChangeStatus(e.target.value)}
              >
                <MenuItem value={'Required'}>Required</MenuItem>
                <MenuItem value={'Optional'}>Optional</MenuItem>
                <MenuItem value={'Draft'}>Draft</MenuItem>
              </Select>
            </FormControl>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              confirmStatusChange();
              setChangeStatus('');
              setChangeStatusDialogOpen(false);
            }}
          >
            Yes
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setChangeStatus('');
              setChangeStatusDialogOpen(false);
            }}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormManagement;
